<app-menu>
  <main class="min-h-[calc(100vh-64px)] bg-slate-50 text-slate-950" data-ui="freight-command-v2">
    <div class="mx-auto max-w-[1500px] space-y-4 px-4 py-5 sm:px-6 lg:px-8">
      <header class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div class="min-w-0">
          <h1 class="m-0 text-2xl font-black tracking-[-0.035em] text-slate-950 sm:text-3xl">
            Freight Command
          </h1>
          <p class="mb-0 mt-1 text-sm text-slate-600">
            Create a spot quote or add rated freight to an existing lading.
          </p>
        </div>

        <nav
          class="inline-flex w-full rounded-xl border border-slate-200 bg-white p-1 shadow-sm lg:w-auto"
          aria-label="Freight Command workflows"
        >
          <a
            *ngFor="let item of navigation"
            class="flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-slate-600 no-underline transition hover:bg-slate-100 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 lg:flex-none"
            [routerLink]="item.route"
            routerLinkActive="bg-slate-950 text-white shadow-sm hover:bg-slate-950 hover:text-white"
          >
            <span>{{ item.label }}</span>
            <span
              *ngIf="item.route === 'freightmancer-ascendant'"
              class="text-[9px] font-black uppercase tracking-[0.16em] opacity-60"
            >
              Ascendant
            </span>
          </a>
        </nav>
      </header>

      <section
        class="shipment-card rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_36px_rgba(15,23,42,0.06)] sm:p-5"
        aria-labelledby="freight-command-shipment-title"
      >
        <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div class="flex items-center gap-2">
              <h2 id="freight-command-shipment-title" class="m-0 text-base font-black text-slate-950">
                Shipment
              </h2>
              <span *ngIf="shipmentReady" class="text-xs font-bold text-emerald-700" aria-label="Shipment ready">
                &#10003; Ready
              </span>
            </div>
            <p class="mb-0 mt-1 text-xs text-slate-500">
              Customer, pickup, and delivery stay with you across both workflows.
            </p>
          </div>

          <button
            *ngIf="hasWorkspaceState"
            type="button"
            class="rounded-lg px-2 py-1 text-xs font-bold text-slate-500 transition hover:bg-rose-50 hover:text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
            title="Clear the shipment, freight, rate, and quote draft"
            (click)="resetWorkspace()"
          >
            Start over
          </button>
        </div>

        <div class="shipment-grid grid gap-4 lg:grid-cols-3">
          <div class="min-w-0">
            <label class="mb-1.5 block text-xs font-bold text-slate-700">Customer</label>
            <app-contact-autocomplete
              class="fc-autocomplete"
              [formControl]="customerControl"
              [type]="'customer_contact_list'"
              placeholder="Search customers"
              panelTheme="light"
              (selected)="onCustomerSelected($event)"
            ></app-contact-autocomplete>
            <p *ngIf="state.customer_name" class="mb-0 mt-1.5 truncate text-xs font-semibold text-slate-500" [title]="state.customer_name">
              {{ state.customer_name }}
            </p>
          </div>

          <div class="min-w-0">
            <div class="mb-1.5 flex items-center justify-between gap-2">
              <label class="text-xs font-bold text-slate-700">Pickup</label>
              <span *ngIf="laneReady('origin')" class="text-[11px] font-semibold text-emerald-700">Ready</span>
            </div>
            <app-contact-autocomplete
              class="fc-autocomplete"
              [formControl]="originControl"
              [type]="'shipper_contact_list'"
              placeholder="Search shippers"
              panelTheme="light"
              (selected)="onLaneContactSelected('origin', $event)"
            ></app-contact-autocomplete>

            <div *ngIf="addressLoading.origin" class="mt-2 flex items-center gap-2 text-xs text-slate-500" aria-live="polite">
              <span class="fc-spinner" aria-hidden="true"></span>
              Loading addresses&hellip;
            </div>

            <label *ngIf="!addressLoading.origin && addressOptions.origin.length > 1" class="mt-2 block">
              <span class="sr-only">Pickup address</span>
              <select
                class="context-select"
                [value]="state.context.origin.contact_address_uuid || ''"
                (change)="onAddressSelected('origin', $event)"
              >
                <option *ngFor="let address of addressOptions.origin; trackBy: trackAddress" [value]="address.value">
                  {{ address.label }}
                </option>
              </select>
            </label>

            <p *ngIf="!addressLoading.origin && addressOptions.origin.length === 1" class="mb-0 mt-2 truncate text-xs text-slate-500" [title]="addressOptions.origin[0].label">
              {{ addressOptions.origin[0].label }}
            </p>
            <p *ngIf="addressError.origin" class="mb-0 mt-2 text-xs font-semibold text-rose-700" role="alert">
              {{ addressError.origin }}
            </p>
          </div>

          <div class="min-w-0">
            <div class="mb-1.5 flex items-center justify-between gap-2">
              <label class="text-xs font-bold text-slate-700">Delivery</label>
              <span *ngIf="laneReady('destination')" class="text-[11px] font-semibold text-emerald-700">Ready</span>
            </div>
            <app-contact-autocomplete
              class="fc-autocomplete"
              [formControl]="destinationControl"
              [type]="'consignee_contact_list'"
              placeholder="Search consignees"
              panelTheme="light"
              (selected)="onLaneContactSelected('destination', $event)"
            ></app-contact-autocomplete>

            <div *ngIf="addressLoading.destination" class="mt-2 flex items-center gap-2 text-xs text-slate-500" aria-live="polite">
              <span class="fc-spinner" aria-hidden="true"></span>
              Loading addresses&hellip;
            </div>

            <label *ngIf="!addressLoading.destination && addressOptions.destination.length > 1" class="mt-2 block">
              <span class="sr-only">Delivery address</span>
              <select
                class="context-select"
                [value]="state.context.destination.contact_address_uuid || ''"
                (change)="onAddressSelected('destination', $event)"
              >
                <option *ngFor="let address of addressOptions.destination; trackBy: trackAddress" [value]="address.value">
                  {{ address.label }}
                </option>
              </select>
            </label>

            <p *ngIf="!addressLoading.destination && addressOptions.destination.length === 1" class="mb-0 mt-2 truncate text-xs text-slate-500" [title]="addressOptions.destination[0].label">
              {{ addressOptions.destination[0].label }}
            </p>
            <p *ngIf="addressError.destination" class="mb-0 mt-2 text-xs font-semibold text-rose-700" role="alert">
              {{ addressError.destination }}
            </p>
          </div>
        </div>
      </section>

      <router-outlet></router-outlet>
    </div>
  </main>
</app-menu>
